import {
  CAD_AREA_BANDS,
  CAD_FILTER_DEFS,
  CAD_FILTER_FORBIDDEN_KEYS,
  CAD_FILTER_KEYS,
  canonicalizeCadListFilters,
  isCadFilterValueActive,
} from '../data/cadFilterContract';
import { filterCadParcelsByQuery, getCadParcelId } from './cadParcelDisplay';
import { filterCadSellableLots, isCadSellableLot } from './cadMasterplan';

function toNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeCurrency(value) {
  const code = String(value || 'USD').trim().toUpperCase();
  return code || 'USD';
}

function matchesBand(option, numeric) {
  if (option.min != null && numeric < option.min) return false;
  if (option.max != null) {
    if (option.maxExclusive ? numeric >= option.max : numeric > option.max) return false;
  }
  return option.min != null || option.max != null;
}

function defByField(field) {
  return CAD_FILTER_DEFS.find((item) => item.field === field);
}

function hasValues(value, field) {
  return isCadFilterValueActive(value, defByField(field) || {});
}

/**
 * Quita keys de catastro/share y las que esta vista no entiende.
 * @param {object|null|undefined} filters
 * @returns {object}
 */
export function sanitizeCadListFilters(filters) {
  const remapped = canonicalizeCadListFilters(filters);
  const next = {};
  CAD_FILTER_KEYS.forEach((key) => {
    if (key in remapped && hasValues(remapped[key], key)) next[key] = remapped[key];
  });
  CAD_FILTER_FORBIDDEN_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

function matchesStock(props, values) {
  const status = props.stock_status || 'available';
  return values.map(String).includes(String(status));
}

function matchesUse(props, values) {
  if (props.use == null || props.use === '') return false;
  return values.map(String).includes(String(props.use));
}

function matchesArea(props, values) {
  const numeric = toNumber(props.area_m2);
  if (numeric == null) return false;
  const wanted = new Set(values.map(String));
  return CAD_AREA_BANDS.some((band) => wanted.has(band.value) && matchesBand(band, numeric));
}

function matchesCurrency(props, values) {
  const code = normalizeCurrency(props.currency);
  return values.map((item) => String(item).toUpperCase()).includes(code);
}

function matchesPrice(props, range, currencyValues) {
  const amount = toNumber(props.price);
  if (amount == null) return false;
  if (Array.isArray(currencyValues) && currencyValues.length > 0) {
    if (!matchesCurrency(props, currencyValues)) return false;
  }
  const min = toNumber(range?.min);
  const max = toNumber(range?.max);
  if (min != null && amount < min) return false;
  if (max != null && amount > max) return false;
  return true;
}

/**
 * ¿La feature cumple los filtros CAD? Keys desconocidas se ignoran.
 * Precio sin número en la ficha → no entra. Precio con moneda en el filtro
 * exige la misma moneda (vacío en ficha = USD).
 * @param {object|null} feature
 * @param {object|null|undefined} filters
 * @returns {boolean}
 */
export function matchesCadParcelFilters(feature, filters) {
  const props = feature?.properties || {};
  const active = sanitizeCadListFilters(filters);

  if (hasValues(active.cad_search, 'cad_search')) {
    const [hit] = filterCadParcelsByQuery([feature], active.cad_search);
    if (!hit) return false;
  }
  if (hasValues(active.stock_status, 'stock_status') && !matchesStock(props, active.stock_status)) {
    return false;
  }
  if (hasValues(active.use, 'use') && !matchesUse(props, active.use)) {
    return false;
  }
  if (hasValues(active.surface, 'surface') && !matchesArea(props, active.surface)) {
    return false;
  }
  if (hasValues(active.currency, 'currency') && !hasValues(active.cad_price, 'cad_price')) {
    if (!matchesCurrency(props, active.currency)) return false;
  }
  if (hasValues(active.cad_price, 'cad_price')
    && !matchesPrice(props, active.cad_price, active.currency)) {
    return false;
  }
  return true;
}

/**
 * Recorta el listado. El overlay puede seguir usando `markCadParcelsForOverlay`.
 * @param {object[]} features
 * @param {object|null|undefined} filters
 * @returns {object[]}
 */
export function filterCadParcelsByFilters(features, filters) {
  const list = filterCadSellableLots(features);
  const active = sanitizeCadListFilters(filters);
  if (Object.keys(active).length === 0) return list;
  return list.filter((feature) => matchesCadParcelFilters(feature, active));
}

/**
 * @param {object[]} features
 * @param {object|null|undefined} filters
 * @returns {Set<string>}
 */
export function matchedCadParcelIds(features, filters) {
  return new Set(
    filterCadParcelsByFilters(features, filters).map((feature) => getCadParcelId(feature)),
  );
}

/**
 * ¿Hay algún filtro CAD activo?
 * @param {object|null|undefined} filters
 * @returns {boolean}
 */
export function hasCadListFilters(filters) {
  return Object.keys(sanitizeCadListFilters(filters)).length > 0;
}

/**
 * Todas las parcelas al overlay: `filter_match` marca las que cumplen.
 * Sin filtro activo deja las features como están.
 * @param {object[]} features
 * @param {object|null|undefined} filters
 * @returns {object[]}
 */
export function markCadParcelsByFilters(features, filters) {
  const list = Array.isArray(features) ? features : [];
  if (!hasCadListFilters(filters)) return list;
  const matchedIds = matchedCadParcelIds(list, filters);
  return list.map((feature) => ({
    ...feature,
    properties: {
      ...(feature?.properties || {}),
      filter_match: isCadSellableLot(feature)
        ? matchedIds.has(getCadParcelId(feature))
        : true,
    },
  }));
}
